import type { Id } from "../core/ids";
import { voiceDiatonicChord, type Degree } from "../theory/diatonic";
import type { MusicalKey } from "../theory/key";
import type { SequencerEvent } from "./events";
import { PPQ, STEP_TICKS, barTicks, type TimeSignature } from "./time";

export const DRUM_LANES = ["kick", "snare", "hat", "clap"] as const;
export type DrumLane = (typeof DRUM_LANES)[number];

/** General MIDI percussion notes (channel 10 convention). */
export const GM_DRUM_NOTE: Record<DrumLane, number> = { kick: 36, snare: 38, hat: 42, clap: 39 };

export interface DrumPattern {
  /** Steps per bar (16 at 4/4). */
  steps: number;
  lanes: Record<DrumLane, boolean[]>;
}

export type ChordRhythm = "whole" | "half" | "quarters" | "eighths";

export interface ProgressionChord {
  degree: Degree;
  seventh: boolean;
}

export interface Progression {
  chords: ProgressionChord[]; // one chord per bar
  octave: number;
  inversion: number;
  rhythm: ChordRhythm;
  bassNote: boolean;
}

/** One bar of drums, repeated `bars` times. */
export function drumEvents(pattern: DrumPattern, trackId: Id, ts: TimeSignature, bars: number): SequencerEvent[] {
  const out: SequencerEvent[] = [];
  const bar = barTicks(ts);
  const steps = Math.min(pattern.steps, bar / STEP_TICKS);
  for (let b = 0; b < bars; b++) {
    for (const lane of DRUM_LANES) {
      const row = pattern.lanes[lane];
      for (let s = 0; s < steps; s++) {
        if (!row[s]) continue;
        const onBeat = s % 4 === 0;
        out.push({
          kind: "note", trackId, tick: b * bar + s * STEP_TICKS, durationTicks: STEP_TICKS,
          note: GM_DRUM_NOTE[lane], velocity: onBeat ? 0.95 : 0.75, pitched: false,
        });
      }
    }
  }
  return out;
}

const RHYTHM_DIVISION: Record<ChordRhythm, number> = { whole: 0, half: PPQ * 2, quarters: PPQ, eighths: PPQ / 2 };

/** Chord events derived from scale degrees in `key` — key changes re-derive the chords. */
export function progressionEvents(prog: Progression, key: MusicalKey, trackId: Id, ts: TimeSignature): SequencerEvent[] {
  const out: SequencerEvent[] = [];
  const bar = barTicks(ts);
  const div = RHYTHM_DIVISION[prog.rhythm] || bar;
  prog.chords.forEach((c, i) => {
    const notes = voiceDiatonicChord(key, c.degree, { octave: prog.octave, seventh: c.seventh, inversion: prog.inversion });
    if (prog.bassNote) {
      const root = voiceDiatonicChord(key, c.degree, { octave: prog.octave - 1 })[0]!;
      notes.unshift(root);
    }
    for (let t = 0; t < bar; t += div) {
      const dur = Math.min(div, bar - t) - PPQ / 16; // short gap so repeated chords re-articulate
      for (const note of notes) {
        out.push({ kind: "note", trackId, tick: i * bar + t, durationTicks: dur, note, velocity: t === 0 ? 0.8 : 0.65, pitched: true });
      }
    }
  });
  return out;
}

export function clickEvents(ts: TimeSignature, bars: number): SequencerEvent[] {
  const out: SequencerEvent[] = [];
  for (let b = 0; b < bars; b++) {
    for (let beat = 0; beat < ts.beatsPerBar; beat++) {
      out.push({ kind: "click", tick: b * barTicks(ts) + beat * PPQ, accent: beat === 0 });
    }
  }
  return out;
}

/** Count-in clicks for `bars` bars, at negative ticks ending at loop tick 0. */
export function countInEvents(ts: TimeSignature, bars: number): { ticks: number; events: SequencerEvent[] } {
  const ticks = bars * barTicks(ts);
  return { ticks, events: clickEvents(ts, bars).map((e) => ({ ...e, tick: e.tick - ticks })) };
}

export function emptyDrumPattern(steps = 16): DrumPattern {
  const row = () => Array.from({ length: steps }, () => false);
  return { steps, lanes: { kick: row(), snare: row(), hat: row(), clap: row() } };
}

/** Resize every lane (keeps existing steps; new steps are off). */
export function resizeDrumPattern(p: DrumPattern, steps: number): DrumPattern {
  const fit = (row: boolean[]) => Array.from({ length: steps }, (_, i) => row[i] ?? false);
  return { steps, lanes: { kick: fit(p.lanes.kick), snare: fit(p.lanes.snare), hat: fit(p.lanes.hat), clap: fit(p.lanes.clap) } };
}
